import { useCallback, useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Body1Strong,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { ButtonLoading } from '@shared/components';
import { SignInContext, UserLabel } from '@modules/users';
import { TDocument, TInitialApprovalUsers } from '@modules/documents';
import { TFile } from '@modules/files';
import { TItemType } from '@modules/items';

import { useUpdateInitialApprovalUsers } from '../api';
import { APPROVAL_PHASE_KEYS, TApprovalPhaseKey } from '../types';
import resolveFunctionNames from '../helpers/resolveFunctionNames.helper';
import ApprovalPhaseUserField from './ApprovalPhaseUserField';
import { buildFunctionsMap, rowsFromInitialApprovalUsers, TFunctionRow } from '../helpers/functionRows.helper';
import useFunctionRows from '../hooks/useFunctionRows';

const PHASE_LABELS: Record<TApprovalPhaseKey, string> = {
  AUTHORING: 'Authors',
  REVIEWING: 'Reviewers',
  APPROVING: 'Approvers',
};

const schema = yup.object({
  authors: yup.array().of(yup.string().required()).default([]),
  reviewers: yup.array().of(yup.string().required()).default([]),
  approvers: yup.array().of(yup.string().required()).default([]),
});
type TFormData = yup.InferType<typeof schema>;

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '820px',
  },
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  section: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px',
  },
});

// --- Read-only content ---

type TReadOnlyContentProps = {
  initialApprovalUsers: TInitialApprovalUsers;
};

const ReadOnlyContent = ({ initialApprovalUsers }: TReadOnlyContentProps) => {
  const { organization } = useContext(SignInContext);
  const isApprovalFunctionsEnabled = organization.featureSettings.approvalFunctions?.enabled;
  const classes = useStyles();

  const groupNameById = useMemo(
    () => new Map(organization.customGroups.map((group) => [group.id, group.name])),
    [organization.customGroups]
  );

  return (
    <div className="flex flex-col space-y-4">
      {APPROVAL_PHASE_KEYS.map((phase) => {
        const entries = initialApprovalUsers[phase] || [];
        return (
          <div key={phase} className={classes.section}>
            <Body1Strong>{PHASE_LABELS[phase]}</Body1Strong>
            {entries.length > 0 ? (
              <div className="mt-3">
                <Table size="small">
                  <TableHeader>
                    <TableRow>
                      {isApprovalFunctionsEnabled && <TableHeaderCell>Functions</TableHeaderCell>}
                      <TableHeaderCell>Users</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry, index) => {
                      const functionNames = resolveFunctionNames(entry.customGroupIds, groupNameById);
                      return (
                        <TableRow key={`${phase}-${entry.user || 'no-user'}-${index}`}>
                          {isApprovalFunctionsEnabled && <TableCell>{functionNames || '-'}</TableCell>}
                          <TableCell>{entry.user ? <UserLabel id={entry.user} /> : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="mt-2">
                <Caption1>No users assigned to this phase.</Caption1>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// --- Edit content ---

type TEditContentProps = {
  item: TDocument | TFile;
  itemType: TItemType;
  initialApprovalUsers?: TInitialApprovalUsers;
  isApprovalFunctionsEnabled: boolean;
  onSaved: () => void;
};

const EditContent = ({
  item,
  itemType,
  initialApprovalUsers,
  isApprovalFunctionsEnabled,
  onSaved,
}: TEditContentProps) => {
  const { organization } = useContext(SignInContext);
  const updateMutation = useUpdateInitialApprovalUsers();
  const classes = useStyles();

  const defaultValues: TFormData = useMemo(
    () => ({
      authors: initialApprovalUsers?.AUTHORING?.filter((e) => e.user).map((e) => e.user!) ?? [],
      reviewers: initialApprovalUsers?.REVIEWING?.filter((e) => e.user).map((e) => e.user!) ?? [],
      approvers: initialApprovalUsers?.APPROVING?.filter((e) => e.user).map((e) => e.user!) ?? [],
    }),
    [initialApprovalUsers]
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const initialAuthorRows = useMemo(
    () => (initialApprovalUsers ? rowsFromInitialApprovalUsers(initialApprovalUsers.AUTHORING) : []),
    [initialApprovalUsers]
  );
  const initialReviewerRows = useMemo(
    () => (initialApprovalUsers ? rowsFromInitialApprovalUsers(initialApprovalUsers.REVIEWING) : []),
    [initialApprovalUsers]
  );
  const initialApproverRows = useMemo(
    () => (initialApprovalUsers ? rowsFromInitialApprovalUsers(initialApprovalUsers.APPROVING) : []),
    [initialApprovalUsers]
  );

  const [authorRows, setAuthorRows] = useFunctionRows(initialAuthorRows, (ids) =>
    setValue('authors', ids, { shouldValidate: true })
  );
  const [reviewerRows, setReviewerRows] = useFunctionRows(initialReviewerRows, (ids) =>
    setValue('reviewers', ids, { shouldValidate: true })
  );
  const [approverRows, setApproverRows] = useFunctionRows(initialApproverRows, (ids) =>
    setValue('approvers', ids, { shouldValidate: true })
  );

  const rowsByPhase: Record<TApprovalPhaseKey, TFunctionRow[]> = {
    AUTHORING: authorRows,
    REVIEWING: reviewerRows,
    APPROVING: approverRows,
  };
  const setRowsByPhase: Record<TApprovalPhaseKey, (rows: TFunctionRow[]) => void> = {
    AUTHORING: setAuthorRows,
    REVIEWING: setReviewerRows,
    APPROVING: setApproverRows,
  };

  const buildPayload = useCallback(
    (data: TFormData): TInitialApprovalUsers => {
      if (isApprovalFunctionsEnabled) {
        const toEntries = (rows: TFunctionRow[]) => {
          const functionsMap = buildFunctionsMap(rows);
          const entries: TInitialApprovalUsers['AUTHORING'] = [];
          for (const [userId, groupIds] of Object.entries(functionsMap)) {
            entries.push({ user: userId, customGroupIds: groupIds });
          }
          for (const row of rows) {
            if (row.userIds.length === 0 && row.customGroupId) {
              entries.push({ customGroupIds: [row.customGroupId] });
            }
          }
          return entries;
        };
        return {
          AUTHORING: toEntries(authorRows),
          REVIEWING: toEntries(reviewerRows),
          APPROVING: toEntries(approverRows),
        };
      }

      const toEntries = (userIds: string[]) => userIds.map((user) => ({ user }));
      return {
        AUTHORING: toEntries(data.authors ?? []),
        REVIEWING: toEntries(data.reviewers ?? []),
        APPROVING: toEntries(data.approvers ?? []),
      };
    },
    [isApprovalFunctionsEnabled, authorRows, reviewerRows, approverRows]
  );

  const onSubmit = (data: TFormData) => {
    updateMutation.mutate(
      {
        id: item._id,
        itemType,
        initialApprovalUsers: buildPayload(data),
      },
      { onSuccess: onSaved }
    );
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className="flex flex-col space-y-3 mt-2">
          {APPROVAL_PHASE_KEYS.map((phase) => (
            <ApprovalPhaseUserField
              key={phase}
              phase={phase}
              functionsEnabled={isApprovalFunctionsEnabled}
              control={control}
              errors={errors}
              rows={rowsByPhase[phase]}
              onRowsChange={setRowsByPhase[phase]}
              customGroups={organization.customGroups}
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <ButtonLoading appearance="primary" onClick={handleSubmit(onSubmit)} loading={updateMutation.isPending}>
          Save
        </ButtonLoading>
        <DialogTrigger disableButtonEnhancement>
          <Button appearance="secondary">Cancel</Button>
        </DialogTrigger>
      </DialogActions>
    </>
  );
};

// --- Main dialog ---

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  item: TDocument | TFile;
  itemType: TItemType;
  canEdit: boolean;
  initialApprovalUsers?: TInitialApprovalUsers;
};

const EditInitialApprovalUsersDialog = ({
  isOpen,
  setIsOpen,
  item,
  itemType,
  canEdit,
  initialApprovalUsers,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const isApprovalFunctionsEnabled = !!organization.featureSettings.approvalFunctions?.enabled;
  const classes = useStyles();

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
      <DialogSurface className={isApprovalFunctionsEnabled ? classes.dialogSurface : ''}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Original approval
          </DialogTitle>
          {canEdit ? (
            <EditContent
              item={item}
              itemType={itemType}
              initialApprovalUsers={initialApprovalUsers}
              isApprovalFunctionsEnabled={isApprovalFunctionsEnabled}
              onSaved={() => setIsOpen(false)}
            />
          ) : (
            <DialogContent className={classes.dialogContent}>
              {initialApprovalUsers && <ReadOnlyContent initialApprovalUsers={initialApprovalUsers} />}
            </DialogContent>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default EditInitialApprovalUsersDialog;
